import { useEffect, useRef, useState } from "react";

/**
 * useReveal — attaches an IntersectionObserver to a ref, adds `in` class the first time it enters.
 * Usage: const ref = useReveal(); <div ref={ref} className="reveal">…</div>
 */
export function useReveal(options = { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") { el.classList.add("in"); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          el.classList.add("in");
          setSeen(true);
          io.disconnect();
        }
      });
    }, options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen]);
  return ref;
}

/**
 * useRevealAll — attaches the effect to every `.reveal` element under a container ref.
 */
export function useRevealAll() {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current || document;
    const targets = root.querySelectorAll(".reveal:not(.in)");
    if (!targets.length) return;
    if (typeof IntersectionObserver === "undefined") {
      targets.forEach((t) => t.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -50px 0px" });
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);
  return rootRef;
}
